import { Button, ButtonProps, CheckboxProps, Flex, Heading } from '@chakra-ui/react';
import { RadioButtonField, RadioFieldMultiple } from '@repo/shared/shared';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { InputField, TextareaField } from '@/shared/ui';

const optionsFields = {
  single: RadioButtonField,
  multiple: RadioFieldMultiple,
};

type TOptionsType = 'single' | 'multiple';

interface IQuizStepData {
  heading: string;
  options?: {
    name: string;
    items: { label: string; value: string }[];
    type: TOptionsType;
    styles?: CheckboxProps;
    autoNext?: boolean;
  };
  input?: {
    name: string;
    placeholder: string;
    requiredAnswer?: string;
  };
  textarea?: {
    name: string;
    placeholder: string;
    required?: boolean;
  };
  buttonText?: string;
  buttonType?: ButtonProps['type'];
}

interface IQuizFormStepProps extends IQuizStepData {
  onNext: () => void;
}

const QuizFormStep = ({
  heading,
  onNext,
  options,
  input,
  textarea,
  buttonText = 'Next',
  buttonType = 'button',
}: IQuizFormStepProps) => {
  const OptionsField = !!options ? optionsFields[options.type] : null;
  const {
    control,
    watch,
    formState: { isSubmitSuccessful, isSubmitting },
  } = useFormContext();

  const answer = options?.name && watch(options.name);
  const inputAnswer = input?.name && watch(input.name);
  const textareaAnswer = textarea?.name && watch(textarea.name);

  let isDisabled = false;

  if (input?.requiredAnswer) {
    isDisabled = !answer || (input.requiredAnswer === answer && !inputAnswer);
  } else if (input) {
    isDisabled = !inputAnswer && !answer?.length;
  } else if (textarea) {
    isDisabled = !textareaAnswer && !!textarea?.required;
  } else if (options) {
    isDisabled = !answer?.length;
  }

  const isAutoNextEnabled = options?.autoNext && options.type === 'single' && !input && !textarea;

  const handleAutoNext = () => {
    if (isAutoNextEnabled) {
      setTimeout(() => {
        onNext();
      }, 150);
    }
  };

  const handleClick = () => {
    if (buttonType === 'submit') return;

    onNext();
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      onNext();
    }
  }, [isSubmitSuccessful]);

  const showNextButton = !isAutoNextEnabled;

  return (
    <Flex
      alignSelf='center'
      flexDir='column'
      gap='xl'
      id='test'
      justifyContent='center'
      maxW='full'
      px='1'
      w={{ base: 'full', lg: 'container.md' }}
    >
      <Heading
        as='h4'
        fontSize='2xl'
        fontWeight='semibold'
        whiteSpace='pre-wrap'
      >
        {heading}
      </Heading>

      {!!OptionsField && !!options && (
        <OptionsField
          containerProps={{
            flexDir: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            w: 'full',
          }}
          control={control}
          h='full'
          name={options.name}
          options={options.items}
          px='sm'
          py='xs'
          textAlign='start'
          w={{ base: 'full', md: '49%' }}
          whiteSpace='pre-line'
          {...options.styles}
          onChange={options.type === 'single' && options.autoNext ? handleAutoNext : undefined}
        />
      )}

      {input && (
        <InputField
          bg='bg.pure'
          name={input.name}
          placeholder={input.placeholder}
        />
      )}

      {textarea && (
        <TextareaField
          as='textarea'
          bg='bg.pure'
          isRequired={textarea.required}
          name={textarea.name}
          placeholder={textarea.placeholder}
        />
      )}

      {showNextButton && (
        <Button
          alignSelf='center'
          bg='bg.pure'
          isDisabled={isDisabled}
          isLoading={isSubmitting}
          type={buttonType}
          variant='outline'
          w='3xs'
          onClick={handleClick}
        >
          {buttonText}
        </Button>
      )}
    </Flex>
  );
};

export { QuizFormStep };
export type { IQuizFormStepProps, IQuizStepData, TOptionsType };
